// What a hint points at.
//
// Two kinds, because the curriculum teaches two kinds of thing: most concepts
// are about a piece or a square, and a few are about a *control*. The square
// is a fact about the board, so it lives in `telemetry/curriculum.ts` where it
// is pure and graded by replaying a log; a selector is a fact about the markup
// and lives here, because the curriculum has no business knowing what the
// buttons are called and a translator's file of sentences even less so.

import type { Side } from "src/engine/rules/board";
import type { GameState } from "src/engine/rules/commands";
import type { Concept } from "src/engine/telemetry/curriculum";
import type { MechanicId } from "src/engine/telemetry/mechanics";

export type HintTarget =
  | { kind: "square"; square: number }
  | { kind: "control"; selector: string };

/**
 * Concepts that are about a control rather than a square.
 *
 * Only where the *first* thing to do is press something. `belts` is the one in
 * the shipped table: a run is drawn with the Build tool up, and a ring round a
 * patch of open ground teaches the destination while leaving out the gesture —
 * which is the half a player does not have yet.
 *
 * Addressed by `data-testid`, the one attribute in this interface that already
 * exists to be named from outside it.
 */
export const HINT_CONTROLS: Partial<Record<MechanicId, string>> = {
  belts: '[data-testid="tool-build"]'
};

/**
 * Where this hint lands, or null when nothing is resolvable.
 *
 * **The square wins where a concept has both.** A piece on the board is a more
 * specific answer than the button that acts on it, and the board is where the
 * player is looking.
 */
export function hintTargetOf(
  concept: Concept,
  state: GameState,
  side: Side
): HintTarget | null {
  const square = concept.subject?.(state, side) ?? null;
  if (square !== null) return { kind: "square", square };
  const selector = HINT_CONTROLS[concept.id];
  return selector ? { kind: "control", selector } : null;
}
